import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { CellFilterService } from 'src/app/common/bhub-common-module/services/cell-filter.service';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { Dataset } from 'src/app/services/models/datasets.models';
import { Subject, takeUntil, debounceTime, switchMap, EMPTY } from 'rxjs';

@Component({
  selector: 'bhub-data-display-cell',
  standalone: false,
  templateUrl: './data-display-cell.component.html',
  styleUrl: './data-display-cell.component.scss'
})
export class DataDisplayCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;

  // UI Properties
  displayValue: string = '';
  displayLabel: string = '';
  displayFormat: 'raw' | 'formatted' | 'currency' | 'percentage' = 'raw';
  displayLayout: 'inline' | 'stacked' = 'inline';

  // Configuration properties
  dataSource: 'none' | 'context' | 'dataset' = 'none';
  datasetId: number | null = null;
  fieldName: string = '';
  contextObjectKey: string = '';
  label: string = '';

  // Inline-style objects bound on the template.
  // Font size applies to both label and value; bold/italic/underline only to label.
  // Alignment is handled via CSS classes on the cell, not inline style, because
  // text-align doesn't reposition flex items — we use justify-content / align-items.
  valueStyle: { [key: string]: string } = {};
  labelStyle: { [key: string]: string } = {};
  textAlign: 'left' | 'center' | 'right' | '' = '';

  // State
  isLoading = false;
  private destroy$ = new Subject<void>();
  private loadData$ = new Subject<{datasetId: number, fieldName: string}>();
  private currentLoadKey: string = '';

  constructor(
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService,
    private cellFilterService: CellFilterService
  ) {
    super();

    // Set up debounced data loading to prevent duplicate requests
    this.loadData$
      .pipe(
        debounceTime(100), // Wait 100ms between requests
        switchMap(({ datasetId, fieldName }) => {
          const loadKey = `${datasetId}-${fieldName}`;
          if (loadKey === this.currentLoadKey) {
            // Don't reload if it's the same dataset and field
            return EMPTY;
          }
          this.currentLoadKey = loadKey;
          this.isLoading = true;
          this.displayValue = 'Loading...';

          // Build filters and load the first entry from the dataset. This cell shows a
          // single value from a limit-1 fetch, so array-shaped contexts stay pinned to
          // their first element rather than matching any of several rows.
          const filters = this.cellFilterService.buildCellFilters(
            this.cell?.dataBinding?.cellFilters,
            this.viewManager,
            null,
            { multiValue: false }
          );
          return this.dynamicTableService.selectEntries(datasetId, 1, undefined, filters);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          if (response.rowData && response.rowData.length > 0) {
            const firstRow = response.rowData[0];
            const rawValue = firstRow[this.fieldName];

            if (rawValue !== null && rawValue !== undefined) {
              this.displayValue = this.formatValue(rawValue);
            } else {
              this.displayValue = '[No data]';
            }
          } else {
            this.displayValue = '[No entries found]';
          }

        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error loading display data (debounced):', error);
          this.displayValue = '[Error loading data]';
          this.currentLoadKey = ''; // Reset on error to allow retry
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected initializeCell(): void {
    this.updateDisplayConfiguration();
    this.initializeContextVariable();
    this.loadDisplayData();
  }

  protected onCellOrDataChange(): void {
    this.updateDisplayConfiguration();
    this.initializeContextVariable();
    this.loadDisplayData();
  }

  protected onCellUpdate(): void {
    this.updateDisplayConfiguration();
    this.initializeContextVariable();
    this.loadDisplayData();
  }

  protected onDataSet(data: any): void {
    // Update display when external data changes
    this.loadDisplayData();
  }

  private initializeContextVariable(): void {
    // Initialize context variable if configured for static display
    const contextVariable = this.getConfigValue('contextVariable', '');

    if (this.dataSource === 'none' && contextVariable && this.viewManager) {
      if (!this.viewManager.contextData) {
        this.viewManager.contextData = {};
      }

      // Store the current display value in the context variable
      // This allows other cells to reference this display value
      this.viewManager.contextData[contextVariable] = this.displayValue || '';
    }
  }

  private updateDisplayConfiguration(): void {
    if (!this.cell) return;

    // Get configuration values
    this.dataSource = this.getConfigValue('dataSource', 'none') as 'none' | 'context' | 'dataset';
    this.displayFormat = this.getConfigValue('displayFormat', 'raw') as 'raw' | 'formatted' | 'currency' | 'percentage';
    this.displayLayout = this.getConfigValue('displayLayout', 'inline') as 'inline' | 'stacked';
    this.label = this.getConfigValue('label', '');

    // Get data binding values
    this.datasetId = this.getDataBindingValue('datasetId') || null;
    this.fieldName = this.getDataBindingValue('fieldName') || '';
    this.contextObjectKey = this.getDataBindingValue('dataObjectKey') || '';

    // Set display label - only show if explicitly configured
    this.displayLabel = this.label;

    // Font size applies to both label and value via inline style.
    // Bold/italic/underline apply only to the label via inline style.
    // Alignment is handled with CSS classes (see textAlign + .align-* in SCSS)
    // because flex items don't respect text-align for positioning.
    const fontSize = this.getConfigValue<number | undefined>('fontSize', undefined);

    const valueStyle: { [key: string]: string } = {};
    if (fontSize) valueStyle['font-size'] = `${fontSize}px`;
    this.valueStyle = valueStyle;

    const labelStyle: { [key: string]: string } = {};
    if (fontSize) labelStyle['font-size'] = `${fontSize}px`;
    const fontWeight = this.getConfigValue<string | undefined>('fontWeight', undefined);
    if (fontWeight) labelStyle['font-weight'] = fontWeight;
    const fontStyle = this.getConfigValue<string | undefined>('fontStyle', undefined);
    if (fontStyle) labelStyle['font-style'] = fontStyle;
    const textDecoration = this.getConfigValue<string | undefined>('textDecoration', undefined);
    if (textDecoration) labelStyle['text-decoration'] = textDecoration;
    this.labelStyle = labelStyle;

    const align = this.getConfigValue<string | undefined>('textAlign', undefined);
    this.textAlign = (align === 'left' || align === 'center' || align === 'right') ? align : '';
  }

  private loadDisplayData(): void {
    if (this.dataSource === 'none') {
      this.displayValue = this.label || '[Static Display]';
      this.currentLoadKey = '';
      // Update context variable after setting display value
      this.initializeContextVariable();
      return;
    }

    if (this.dataSource === 'context') {
      this.loadContextData();
      return;
    }

    if (this.dataSource === 'dataset') {
      if (!this.datasetId || !this.fieldName) {
        this.displayValue = '[No data configured]';
        this.currentLoadKey = ''; // Reset the key when no data is configured
        return;
      }

      // Emit to the debounced Subject instead of making direct HTTP call
      this.loadData$.next({
        datasetId: this.datasetId,
        fieldName: this.fieldName
      });
    }
  }

  private loadContextData(): void {

    if (!this.contextObjectKey || !this.fieldName || !this.viewManager?.contextData) {
      this.displayValue = '[No context data configured]';
      return;
    }

    const contextObject = this.viewManager.contextData[this.contextObjectKey];
    if (!contextObject) {
      this.displayValue = `[${this.contextObjectKey} not found]`;
      return;
    }

    let value: any;

    if (Array.isArray(contextObject)) {
      // If it's an array, get the field from the first item
      if (contextObject.length > 0 && contextObject[0] && typeof contextObject[0] === 'object') {
        value = contextObject[0][this.fieldName];
      } else {
        this.displayValue = '[No data in array]';
        return;
      }
    } else if (typeof contextObject === 'object' && contextObject !== null) {
      // If it's a single object, get the field value
      value = contextObject[this.fieldName];
    } else {
      this.displayValue = '[Invalid context object]';
      return;
    }

    if (value === null || value === undefined) {
      this.displayValue = `[${this.fieldName} not found]`;
    } else {
      this.displayValue = this.formatValue(value);
    }
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined) {
      return '[No data]';
    }

    const stringValue = String(value);

    switch (this.displayFormat) {
      case 'currency':
        const numValue = parseFloat(stringValue);
        if (isNaN(numValue)) {
          return stringValue;
        }
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(numValue);

      case 'percentage':
        const percentValue = parseFloat(stringValue);
        if (isNaN(percentValue)) {
          return stringValue;
        }
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(percentValue / 100);

      case 'formatted':
        const formattedValue = parseFloat(stringValue);
        if (isNaN(formattedValue)) {
          return stringValue;
        }
        return new Intl.NumberFormat('en-US').format(formattedValue);

      case 'raw':
      default:
        return stringValue;
    }
  }
}
